import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Megaphone, Edit, Save, X, Lock } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PresenterUpdatesSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);

  const fetchUpdates = async () => {
    try {
      const response = await axios.get(`${API}/presenter-updates`);
      setData(response.data);
      setEditContent(response.data.content);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching presenter updates:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, []);

  const handleEditClick = () => {
    setShowPasswordPrompt(true);
  };

  const handlePasswordSubmit = () => {
    if (password === 'editor') {
      setIsEditing(true);
      setShowPasswordPrompt(false);
      setPassword('');
      setLoading(false); // Ensure loading is false when entering edit mode
      toast.success('Edit mode enabled');
    } else {
      toast.error('Invalid password');
      setPassword('');
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/presenter-updates`, null, {
        params: {
          password: 'editor',
          content: editContent
        }
      });
      
      setIsEditing(false);
      await fetchUpdates();
      toast.success('Updates saved successfully');
    } catch (error) {
      console.error('Error saving updates:', error);
      toast.error('Failed to save updates');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setShowPasswordPrompt(false);
    setPassword('');
    setEditContent(data?.content || '');
  };

  return (
    <div className="content-card content-card-large" data-testid="presenter-updates-section">
      <div className="card-header">
        <h2>
          <Megaphone size={20} className="inline" /> Presenter Updates & Promos
        </h2>
        {!isEditing && !showPasswordPrompt && (
          <Button 
            onClick={handleEditClick} 
            variant="outline" 
            size="sm"
            className="edit-updates-btn"
            title="Edit updates (password required)"
          >
            <Edit size={16} />
          </Button>
        )}
      </div>

      {showPasswordPrompt && (
        <div className="password-prompt">
          <div className="password-input-container">
            <Lock size={18} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              placeholder="Enter editor password"
              className="password-input"
              autoFocus
            />
            <Button onClick={handlePasswordSubmit} size="sm">
              Unlock
            </Button>
            <Button onClick={handleCancel} variant="ghost" size="sm">
              <X size={16} />
            </Button>
          </div>
        </div>
      )}

      <ScrollArea className="card-content">
        {loading ? (
          <p className="loading-text">Loading updates...</p>
        ) : isEditing ? (
          <div className="edit-mode">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="edit-textarea"
              placeholder="Enter presenter updates, changes, promos, reminders..."
              rows={12}
              style={{
                backgroundColor: '#ffffff',
                color: '#000000'
              }}
            />
            <div className="edit-actions">
              <Button onClick={handleSave} className="save-btn">
                <Save size={16} /> Save Updates
              </Button>
              <Button onClick={handleCancel} variant="outline">
                <X size={16} /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="updates-content">
            <div className="updates-text">
              {data?.content ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>{data.content}</p>
              ) : (
                <p className="no-updates">No updates at this time</p>
              )}
            </div>
            {data?.last_updated && (
              <div className="updates-footer">
                <p className="last-updated">
                  Last updated: {new Date(data.last_updated).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default PresenterUpdatesSection;
